'use client';

import type { ReactNode } from 'react';
import { styled } from 'styled-components';
import cs from 'classnames';
import TextLink from '../components/TextLink';
import AccordionList from '../components/AccordionList';
import Media from '../components/Media';
import * as Styled from '../../lib/styles';
import type { CtaProps, ImageProps } from '@types';
type MediaAccordionBaseProps = {
  /**
   * What the H3 element says
   */
  headline: string;

  /**
   * Descriptive paragraph
   */
  description: string;

  /**
   * Accordion components passed in as children
   */
  children: ReactNode;
  /**
   * Optional CTA for the module
   */
  cta?: CtaProps;
  /**
   * Layout
   * - standard (default layout)
   * - wide for large video
   *
   * This is the layout axis only; it is independent of `kind`, so a `wide`
   * image and an `sd` video are both valid.
   */
  variant?: 'sd' | 'wide';
};

type MediaAccordionVideoProps = MediaAccordionBaseProps & {
  kind: 'video';
  /**
   * The hashed ID of the video to display
   */
  hashedId: string;
};

type MediaAccordionImageProps = MediaAccordionBaseProps & {
  kind: 'image';
  /**
   * Featured image to display
   */
  image: ImageProps;
};

type MediaAccordionProps = MediaAccordionVideoProps | MediaAccordionImageProps;

const MediaAccordionHeader = styled.div<{ $href?: string }>`
  padding-bottom: ${({ $href, theme }) =>
    $href ? theme.spacing['3'] : theme.spacing['1.5']};
  text-align: center;

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-bottom: ${({ $href, theme }) =>
      $href ? theme.spacing['4'] : theme.spacing['2.5']};
  }
`;

const MediaAccordionContainer = styled.div`
  padding: ${({ theme }) => `${theme.spacing['4']} 0`};

  .mediaAccordionSplit {
    width: 100%;
    margin: 0;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    display: grid;
    ${({ theme }) => theme.grid.columns.large}

    ${MediaAccordionHeader} {
      grid-column: 3 / 11;
    }

    .mediaAccordionWrapper,
    .mediaAccordionSplit {
      grid-row: 2;
    }

    &.sd {
      .mediaAccordionWrapper {
        grid-column: 2 / span 5;
      }

      .mediaAccordionSplit {
        grid-column: 7 / 12;
      }
    }

    &.wide {
      .mediaAccordionWrapper {
        grid-column: 1 / span 5;
      }

      .mediaAccordionSplit {
        grid-column: 6 / 13;
      }
    }
  }
`;

const MediaAccordion = (props: MediaAccordionProps) => {
  const { children, cta = null, description, headline, variant = 'sd' } = props;

  return (
    <MediaAccordionContainer className={cs(variant)}>
      <MediaAccordionHeader $href={cta?.href}>
        <h2>{headline}</h2>
        <Styled.BodyMd>{description}</Styled.BodyMd>
        {cta ? <TextLink cta={cta} /> : undefined}
      </MediaAccordionHeader>

      {props.kind === 'video' ? (
        <Media className="mediaAccordionSplit" hashedId={props.hashedId} />
      ) : (
        <Media
          alt={props.image.alt}
          className="mediaAccordionSplit"
          height={props.image.height}
          src={props.image.src}
          width={props.image.width}
        />
      )}
      <AccordionList className="mediaAccordionWrapper">
        {children}
      </AccordionList>
    </MediaAccordionContainer>
  );
};

export default MediaAccordion;
